import { BrowserStorage } from '@atc/browser-storage';

/**
 * @typedef {Object} cacheEvent
 * @property {String} action
 * @property {String} cacheType
 * @property {String} id
 */
export const rootKey = 'ATC';
export const sixtyDaysInMs = 5184000000; // 60 * 24 * 60 * 60 * 1000

/**
 * Handles storing and caching personalization locally without authentication
 * @module BrowserCache
 */
export default class BrowserCache {
  constructor(options) {
    if (options === void 0) {
      options = {};
    }
    this.storage = new BrowserStorage(options.rootKey, {
      persist: true
    });
    this.options = {
      maxSize: options.maxSize || 50,
      newItemKey: options.newItemKey
    };
  }

  /**
   * @alias module:BrowserCache
   * @return {Array} items list of search object
   */
  getItems() {
    return this.storage.getItem() || [];
  }

  /**
   * Replace any current cached items with a new set
   * @alias module:LocalCache
   * @param {Array} items list of search object
   * @return {Array}
   */
  setItems(items) {
    if (items === void 0) {
      items = [];
    }
    this.storage.setItem(items);
    return items;
  }

  /**
   * @param items
   * @alias module:BrowserCache
   * @returns {Array} items list of search object
   */
  removeExpiredItems(items) {
    if (items === void 0) {
      items = [];
    }
    const now = new Date().getTime();
    return items.filter(item => item.timestamp > now - sixtyDaysInMs);
  }

  /**
   * remove expired items and update local storage
   * @returns {Array}
   */
  refreshItems() {
    const items = this.getItems();
    return this.setItems(this.removeExpiredItems(items));
  }

  /**
   * true if item does NOT exist in local storage
   * by comparing "heading", we determine the it is a new search
   *
   * @alias module:BrowserCache
   * @param item
   * @returns {Boolean}
   */
  isNewItem(item, isNew) {
    if (item === void 0) {
      item = {};
    }
    if (isNew === void 0) {
      isNew = true;
    }
    if (isNew) {
      return currentItem => item[this.options.newItemKey] !== currentItem[this.options.newItemKey];
    }
    return currentItem => item[this.options.newItemKey] === currentItem[this.options.newItemKey];
  }

  /**
   * @alias module:BrowserCache
   * @param item
   * @returns {Array}
   */
  getDedupedItems(item) {
    if (item === void 0) {
      item = {};
    }
    return this.getItems().filter(this.isNewItem(item)).slice(0, this.options.maxSize);
  }

  /**
   * @alias module:BrowserCache
   * @param item
   * @returns {Array}
   */
  getDupedItem(item) {
    if (item === void 0) {
      item = {};
    }
    const dupedItem = this.getItems().find(this.isNewItem(item, false));
    const newItem = {
      ...dupedItem,
      ...item
    };
    return newItem;
  }

  /**
   * @alias module:BrowserCache
   * @param item
   * @return {Object}
   */
  addItem(item) {
    if (item === void 0) {
      item = {};
    }
    const deduped = this.getDedupedItems(item);
    const duped = this.getDupedItem(item);
    const newItems = [duped, ...deduped];
    this.setItems(newItems);
    return {
      action: 'add',
      items: newItems
    };
  }

  /**
   * @alias module:BrowserCache
   * @param item
   * @return {Object}
   */
  removeItem(item) {
    if (item === void 0) {
      item = {};
    }
    const currentItems = this.getItems();
    const newItems = currentItems.filter(currentItem => item[this.options.newItemKey] !== currentItem[this.options.newItemKey]);
    this.setItems(newItems);
    return {
      action: 'remove',
      items: newItems
    };
  }

  /**
   * @alias module:BrowserCache
   * @param item
   * @return {Object}
   */
  editItem(_ref) {
    let {
      index = 0,
      ...data
    } = _ref;
    const currentItems = this.getItems();
    const newItems = [...currentItems];
    newItems[index] = {
      ...currentItems[index],
      ...data
    };
    this.setItems(newItems);
    return {
      action: 'edit',
      items: newItems
    };
  }

  /**
   * @alias module:BrowserCache
   * @return {this}
   */
  clearItems() {
    this.storage.removeItem();
    return this;
  }
}